import { useState } from 'react';
import { Card, Col, Row } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faStar } from '@fortawesome/free-solid-svg-icons';
import IconItem from '@/components/common/icon/IconItem';
import FalconCardHeader from '@/components/common/FalconCardHeader';
import { BrandFavicon } from '../BrandFavicon';
import { MarkdownContent } from './MarkdownContent';
import { ModelSwitch } from './ModelSwitch';
import { CrmStatsCardSkeleton, Skeleton } from '../common/Skeleton';
import AgGridSkeleton from '@/app/common/components/skeleton/AgGridSkeleton';
import CrmStats, { type CrmStatItem } from './CrmStats';
import { PromptOverviewTopBrandsTable } from './PromptOverviewTopBrandsTable';
import { OwnBrandOverviewCard } from '../dashboard/OwnBrandOverviewCard';
import { ModelBrandComparisonCard } from '../dashboard/ModelBrandComparisonCard';
import DataLoadError from '@/components/errors/DataLoadError';
import type {
  PromptOverview,
  PromptOverviewKpi,
  PromptOverviewLastResponse,
} from '../../types';

function normalizeSparkline(values: number[]): number[] {
  const spark = values.filter((value) => Number.isFinite(value));
  if (spark.length >= 2) return spark;
  if (spark.length === 1) return [spark[0], spark[0]];
  return [0, 0];
}

function toCrmStat(
  id: number,
  title: string,
  kpi: PromptOverviewKpi,
  icon: CrmStatItem['icon'],
  color: string,
  invert = false,
): CrmStatItem {
  const spark = normalizeSparkline(kpi.sparkline);
  // Only one run so far: nothing to compare against (spark is padded to 2 points for the chart).
  const hasHistory = kpi.sparkline.filter((value) => Number.isFinite(value)).length >= 2;
  const first = spark[0];
  const last = spark[spark.length - 1];
  const sparkDiff = invert ? first - last : last - first;

  let target = '';
  let trend: 'up' | 'down' | 'flat' | null = null;
  if (hasHistory) {
    if (sparkDiff === 0) {
      target = 'بدون تغییر';
      trend = 'flat';
    } else {
      target = `${first} vs ${last}`;
      trend = sparkDiff < 0 ? 'down' : 'up';
    }
  } else if (kpi.delta === 0) {
    target = 'بدون تغییر';
    trend = 'flat';
  } else if (kpi.delta != null) {
    target = `${kpi.delta > 0 ? '+' : ''}${kpi.delta}`;
    trend = kpi.delta < 0 ? 'down' : 'up';
  }

  return {
    id,
    title,
    amount: kpi.value ?? 0,
    target,
    icon,
    caret: trend === 'down' ? 'caret-down' : trend === 'up' ? 'caret-up' : undefined,
    color,
    caretColor: trend === 'down' ? 'danger' : 'success',
    data: spark,
  };
}

function PromptChartSkeleton({
  titleWidth,
  horizontal = false,
  withIcon = false,
}: {
  titleWidth: string;
  horizontal?: boolean;
  withIcon?: boolean;
}) {
  return (
    <Card className="h-100 shadow-sm d-flex flex-column placeholder-glow">
      <FalconCardHeader
        light
        titleTag="h6"
        className="py-3"
        title={
          <span className="d-inline-flex align-items-center gap-2">
            {withIcon ? (
              <Skeleton className="rounded-circle flex-shrink-0" style={{ width: '1.5rem', height: '1.5rem' }} />
            ) : null}
            <Skeleton style={{ height: '1rem', width: titleWidth }} />
          </span>
        }
      />
      <Card.Body className="pt-3 d-flex flex-column flex-grow-1">
        {horizontal ? (
          <div
            className="d-flex flex-column justify-content-center gap-3 flex-grow-1 px-2"
            dir="ltr"
            style={{ minHeight: 240 }}
          >
            {['84%', '67%', '52%', '38%'].map((width) => (
              <div key={width} className="d-flex align-items-center gap-3">
                <Skeleton style={{ height: '0.65rem', width: '4rem' }} />
                <Skeleton className="rounded-pill" style={{ height: '1rem', width }} />
              </div>
            ))}
          </div>
        ) : (
          <div
            className="d-flex align-items-end justify-content-around gap-2 flex-grow-1 px-2"
            dir="ltr"
            style={{ minHeight: 240 }}
          >
            {['42%', '68%', '52%', '82%', '60%'].map((height, index) => (
              <div
                key={height}
                className="d-flex flex-column align-items-center justify-content-end gap-2 h-100 flex-grow-1"
              >
                <Skeleton className="rounded-top w-100" style={{ height, maxWidth: '2rem' }} />
                <Skeleton style={{ height: '0.65rem', width: index % 2 ? '3.5rem' : '2.5rem' }} />
              </div>
            ))}
          </div>
        )}
        <div className="d-flex flex-wrap justify-content-center gap-3 mt-3">
          {Array.from({ length: horizontal ? 3 : 2 }).map((_, index) => (
            <span key={index} className="d-inline-flex align-items-center gap-2">
              <Skeleton className="rounded-circle" style={{ width: '0.6rem', height: '0.6rem' }} />
              <Skeleton style={{ height: '0.65rem', width: index === 0 ? '5rem' : '4rem' }} />
            </span>
          ))}
        </div>
      </Card.Body>
    </Card>
  );
}

function PromptLastResponseSkeleton() {
  return (
    <Card className="shadow-sm placeholder-glow">
      <FalconCardHeader
        light
        titleTag="h6"
        className="py-2"
        title={<Skeleton style={{ height: '0.85rem', width: '9rem' }} />}
        endEl={
          <div className="d-flex gap-1">
            {Array.from({ length: 2 }).map((_, index) => (
              <Skeleton key={index} className="rounded-2" style={{ height: '2rem', width: '5rem' }} />
            ))}
          </div>
        }
      />
      <Card.Body>
        <div className="d-flex flex-column gap-2" style={{ maxWidth: '42rem' }}>
          <Skeleton className="w-100" style={{ height: '0.75rem' }} />
          <Skeleton className="w-100" style={{ height: '0.75rem' }} />
          <Skeleton style={{ height: '0.75rem', width: '76%' }} />
          <Skeleton style={{ height: '0.75rem', width: '58%' }} />
          <Skeleton style={{ height: '0.75rem', width: '68%' }} />
        </div>
        <div className="d-flex flex-wrap align-items-center gap-2 mt-3 pt-3 border-top">
          <Skeleton style={{ height: '0.65rem', width: '6rem' }} />
          {Array.from({ length: 3 }).map((_, index) => (
            <Skeleton key={index} className="rounded-pill" style={{ height: '1.5rem', width: '5rem' }} />
          ))}
        </div>
      </Card.Body>
    </Card>
  );
}

function PromptOverviewSkeleton() {
  return (
    <div className="d-flex flex-column gap-3" aria-busy="true">
      <CrmStatsCardSkeleton count={4} />
      <Row className="g-3">
        <Col xs={12} md={6}>
          <PromptChartSkeleton titleWidth="10rem" />
        </Col>
        <Col xs={12} md={6}>
          <PromptChartSkeleton titleWidth="12rem" horizontal withIcon />
        </Col>
      </Row>
      <AgGridSkeleton height="280px" />
      <PromptLastResponseSkeleton />
    </div>
  );
}

export function PromptOverviewPanel({
  overview,
  loading,
  error,
  onRetry,
}: {
  overview: PromptOverview | null;
  loading: boolean;
  error: boolean;
  onRetry: () => void;
}) {
  if (error) {
    return (
      <DataLoadError compact onRetry={onRetry} isRetrying={loading} />
    );
  }

  if (loading) {
    return <PromptOverviewSkeleton />;
  }

  if (!overview) return null;

  const competitors = overview.topBrands.filter((brand) => brand.relationship !== 'you');

  const crmStats = [
    toCrmStat(1, 'نرخ استناد', overview.kpis.citationRate, 'link', 'primary'),
    toCrmStat(2, 'میانگین جایگاه', overview.kpis.avgPosition, 'chart-line', 'info', true),
    toCrmStat(3, 'سهم حضور در پاسخ‌ها', overview.kpis.presenceShare, 'comment', 'success'),
    toCrmStat(4, 'دیده‌شدن در AI', overview.kpis.visibility, 'eye', 'warning'),
  ];

  return (
    <div className="d-flex flex-column gap-3">
      <CrmStats stats={crmStats} />

      <Row className="g-3">
        <Col xs={12} md={6}>
          {/* No competitor list: hides the card's "compare with competitor" dropdown. */}
          <ModelBrandComparisonCard models={overview.modelBrands ?? []} competitors={[]} plotHeight={240} />
        </Col>
        <Col xs={12} md={6}>
          <OwnBrandOverviewCard
            ownBrand={overview.ownBrand}
            competitors={competitors}
            trackedCompetitors={[]}
            plotHeight={240}
            title={
              <span className="d-inline-flex align-items-center gap-2">
                <IconItem
                  tag="div"
                  icon="eye"
                  bg="soft-info"
                  color="info"
                  size="sm"
                  iconClass="fs--2"
                  className="shadow-none"
                />
                سهم از دیده‌شدن رقبای برتر
              </span>
            }
          />
        </Col>
      </Row>

      <Card className="overflow-hidden ag-grid-in-card">
        <FalconCardHeader
          light
          titleTag="h6"
          className="py-2"
          title={
            <span className="d-inline-flex align-items-center gap-2">
              <FontAwesomeIcon icon={faStar} className="text-warning" />
              برندهای پرتکرار در پاسخ‌ها
            </span>
          }
        />
        <Card.Body className="p-0">
          <PromptOverviewTopBrandsTable brands={overview.topBrands} />
        </Card.Body>
      </Card>

      <LastResponseCard
        key={overview.lastResponses.map((item) => `${item.modelId}:${item.executedAt}`).join('|') || 'none'}
        responses={overview.lastResponses}
      />
    </div>
  );
}

function newestModelId(responses: PromptOverviewLastResponse[]): string | null {
  return [...responses].sort((a, b) =>
    (b.executedAt ?? '').localeCompare(a.executedAt ?? ''),
  )[0]?.modelId ?? null;
}

function LastResponseCard({
  responses,
}: {
  responses: PromptOverviewLastResponse[];
}) {
  const [activeId, setActiveId] = useState<string | null>(() =>
    newestModelId(responses),
  );
  const selected =
    responses.find((item) => item.modelId === activeId) ?? responses[0];

  if (!selected) return null;

  return (
    <Card>
      <FalconCardHeader
        light
        titleTag="h6"
        className="py-2"
        title="آخرین پاسخ تولیدشده"
        endEl={
          <ModelSwitch
            items={responses.map((item, index) => ({
              id: item.modelId ?? `${item.modelName ?? 'مدل'}-${index}`,
              name: item.modelName ?? 'مدل',
              iconSvg: item.iconSvg,
              status: 'completed',
            }))}
            activeId={selected.modelId}
            onChange={setActiveId}
          />
        }
      />
      <Card.Body>
        <div
          className="fs--1 text-800"
          style={{ maxHeight: '16rem', overflow: 'auto' }}
        >
          <MarkdownContent text={selected.text} />
        </div>
        {selected.sources.length > 0 ? (
          <div className="d-flex flex-wrap align-items-center gap-2 mt-3 pt-3 border-top">
            <span className="fs--2 text-500">منابع و Citationها:</span>
            {selected.sources.map((source) => (
              <a
                key={source.host}
                href={source.url ?? `https://${source.host}`}
                target="_blank"
                rel="noopener noreferrer"
                className="badge badge-subtle-secondary rounded-pill d-inline-flex align-items-center gap-1 text-decoration-none fw-normal"
              >
                <BrandFavicon host={source.host} name={source.host} size="xs" />
                {source.host}
              </a>
            ))}
          </div>
        ) : null}
      </Card.Body>
    </Card>
  );
}
